import { useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/config";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchLocations = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/locations/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching locations:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleCreate = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!name.trim()) {
      setFormError("Location name is required.");
      return;
    }

    const payload = { name: name.trim() };

    fetch(`${API_BASE_URL}/api/locations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to create location.";
          if (data && data.name && Array.isArray(data.name)) {
            msg = data.name[0];
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then(() => {
        setFormSuccess("Location created.");
        setName("");
        fetchLocations();
      })
      .catch((err) => setFormError(err.message || "Failed to create location."));
  };

  const startEdit = (loc) => {
    setEditingId(loc.id);
    setEditingName(loc.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = (e) => {
    e.preventDefault();
    if (!editingName.trim()) return;

    const payload = {
      name: editingName.trim(),
    };

    fetch(`${API_BASE_URL}/api/locations/${editingId}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to update location.";
          if (data && data.name && Array.isArray(data.name)) {
            msg = data.name[0];
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then((updated) => {
        setLocations((prev) =>
          prev.map((l) => (l.id === updated.id ? updated : l))
        );
        cancelEdit();
      })
      .catch((err) => alert(err.message || "Failed to update location."));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this location?")) return;
    setDeleteLoading(true);

    fetch(`${API_BASE_URL}/api/locations/${id}/`, {
      method: "DELETE",
      headers: authHeaders(),
    })
      .then((res) => {
        if (res.status !== 204 && !res.ok) {
          throw new Error("Failed to delete location.");
        }
        setLocations((prev) => prev.filter((l) => l.id !== id));
      })
      .catch((err) => alert(err.message || "Failed to delete location."))
      .finally(() => setDeleteLoading(false));
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Locations / Gudang</h1>

      <div className="glass-card p-4 mb-4">
        <h2 className="text-sm font-semibold mb-3">Add Location</h2>
        <form onSubmit={handleCreate} className="grid gap-2 max-w-xl">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
              placeholder="e.g. Gudang A"
            />
          </div>
          <div>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
            >
              Save
            </button>
          </div>
        </form>
        {(formError || formSuccess) && (
          <div className="mt-2 text-xs">
            {formError && <div className="text-red-600">{formError}</div>}
            {formSuccess && (
              <div className="text-green-600">{formSuccess}</div>
            )}
          </div>
        )}
      </div>

      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">
                Name
              </th>
              <th className="!text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-4 py-4 text-center text-muted" colSpan={2}>
                  Loading...
                </td>
              </tr>
            )}
            {!loading && locations.length === 0 && (
              <tr>
                <td className="px-4 py-4 text-center text-muted" colSpan={2}>
                  No locations.
                </td>
              </tr>
            )}
            {!loading &&
              locations.map((loc) => (
                <tr key={loc.id} className="border-t">
                  <td className="px-4 py-2">
                    {editingId === loc.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="w-full rounded-md border border-white/50 dark:border-white/15 px-2 py-1 text-sm"
                      />
                    ) : (
                      loc.name
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {editingId === loc.id ? (
                      <>
                        <button
                          className="text-xs px-2 py-1 mr-2 rounded border border-white/50 dark:border-white/15"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="text-xs px-2 py-1 mr-2 rounded border border-white/50 dark:border-white/15"
                          onClick={() => startEdit(loc)}
                        >
                          Edit
                        </button>
                        <button
                          disabled={deleteLoading}
                          className="text-xs px-2 py-1 rounded bg-red-500 text-white"
                          onClick={() => handleDelete(loc.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Locations;
